import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCandidatePortalAccess } from "@/hooks/use-candidate-portal-access";
import { DashboardSkeleton } from "@/components/LoadingSkeleton";

interface RouteGuardProps {
  children: React.ReactNode;
}

export function AuthGuard({ children }: RouteGuardProps) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <DashboardSkeleton />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/**
 * Requires the user to be logged in AND to have accepted the undertaking.
 *
 * NOTE: Acceptance is currently stored in localStorage only (keyed by staffId).
 * This means clearing browser storage re-triggers the undertaking page, which is
 * intentional — it acts as a per-device acceptance gate. If a server-side record
 * of acceptance is required in future, add a POST /candidate/undertaking endpoint
 * and persist the flag there alongside the localStorage flag.
 */
export function TermsGuard({ children }: RouteGuardProps) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <DashboardSkeleton />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;

  const accepted = Boolean(user.undertakingAcceptedAt);

  if (!accepted) return <Navigate to="/agree" replace />;
  return <>{children}</>;
}

export function PublicOnly({ children }: RouteGuardProps) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <DashboardSkeleton />
      </div>
    );
  if (user) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function ApplicantActionsGuard({ children }: RouteGuardProps) {
  const { user, loading: authLoading } = useAuth();
  const { loading, canUseApplicantActions } = useCandidatePortalAccess();

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <DashboardSkeleton />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!canUseApplicantActions) return <Navigate to="/" replace />;
  return <>{children}</>;
}
